import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Card, CardContent, Chip, Grid, Stack, Typography, CardActionArea, Divider } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TuneIcon from "@mui/icons-material/Tune";
import { MarketingShell } from "../components/marketing-shell";
import { computeBundlePreview, EXCLUSIVE_FULL_BUNDLE_BONUS_BRL, FREE_MODULE_IDS, MODULAR_ADVANTAGES, MODULE_PLANS } from "../data/plans";

type VariantGroup = {
  id: string;
  title: string;
  description: string;
  variants: Array<(typeof MODULE_PLANS)[number]>;
};

const variantGroups: VariantGroup[] = [
  {
    id: "products_sales",
    title: "Capacidade de Catálogo e Vendas",
    description: "Escolha a variante que atende ao volume da sua operação comercial.",
    variants: MODULE_PLANS.filter((m) => m.id.startsWith("products_sales_")),
  },
  {
    id: "users_control",
    title: "Tamanho da sua Equipe",
    description: "Quantos acessos simultâneos sua loja precisa hoje?",
    variants: MODULE_PLANS.filter((m) => m.id.startsWith("users_")),
  },
  {
    id: "customers_credit",
    title: "Clientes e Fiado",
    description: "Controle a carteira de clientes e o volume de fiado.",
    variants: MODULE_PLANS.filter((m) => m.id.startsWith("customers_credit_")),
  },
];

const freeFixedIds = ["cash_register", "supplier_categories", "statistics_reports"];
const freeFixedModules = MODULE_PLANS.filter((m) => freeFixedIds.includes(m.id));
const paidOrOptionalAddons = MODULE_PLANS.filter((m) =>
  ["inventory_control", "accounts_payable", "bulk_register", "commissions"].includes(m.id)
);

const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";

export const MarketingPlansPage = () => {
  const navigate = useNavigate();
  // Módulos gratuitos são pré-selecionados e não podem ser removidos
  const freeVariantDefaults = useMemo<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    for (const group of variantGroups) {
      const freeVariant = group.variants.find((v) => v.monthlyPrice === 0);
      if (freeVariant) defaults[group.id] = freeVariant.id;
    }
    return defaults;
  }, []);

  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>(freeVariantDefaults);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

  useEffect(() => {
    document.title = "Planos — Symplou";
  }, []);

  const selectedIds = useMemo(() => {
    const variants = Object.values(selectedVariants).filter(Boolean);
    const freeAlwaysOn = FREE_MODULE_IDS.filter((id) => freeFixedIds.includes(id));
    return Array.from(new Set([...variants, ...freeAlwaysOn, ...selectedAddons]));
  }, [selectedVariants, selectedAddons]);

  const preview = useMemo(() => computeBundlePreview(selectedIds), [selectedIds]);

  // Constantes de pacotes fixos com as variações exatas
  const fullBundleIds = useMemo(() => [
    "products_sales_unlimited",
    "users_unlimited",
    "customers_credit_unlimited",
    "inventory_control",
    "accounts_payable",
    "bulk_register",
    "commissions",
    ...freeFixedIds
  ], []);
  const fullBundlePreview = useMemo(() => computeBundlePreview(fullBundleIds), [fullBundleIds]);

  const freeBundleIds = useMemo(() => [
    "products_sales_basic",
    "users_basic",
    "customers_credit_basic",
    ...freeFixedIds
  ], []);

  const toggleAddon = (id: string) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleNavigateFree = () => {
    const query = `?modules=${encodeURIComponent(freeBundleIds.join(","))}&discount=0&total=0&bonus=0`;
    navigate(`/assinar${query}`);
  };

  const handleNavigateFull = () => {
    const query = `?modules=${encodeURIComponent(fullBundleIds.join(","))}&discount=${fullBundlePreview.discountPct}&total=${fullBundlePreview.finalMonthlyTotal.toFixed(2)}&bonus=1`;
    navigate(`/assinar${query}`);
  };

  const suggestedRoute = selectedIds[0] || "custom_bundle";
  const hasExclusiveBonus = selectedIds.length === fullBundleIds.length;
  const customQuery = `?modules=${encodeURIComponent(
    selectedIds.join(",")
  )}&discount=${preview.discountPct}&total=${preview.finalMonthlyTotal.toFixed(
    2
  )}&bonus=${hasExclusiveBonus ? "1" : "0"}`;

  return (
    <MarketingShell>
      <Box sx={{ maxWidth: 1120, mx: "auto", pb: 5, px: { xs: 2, md: 0 } }}>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            sx={{
              mb: 1.2,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              fontWeight: 800,
              color: "#14b8a6",
              fontSize: "0.8rem",
            }}
          >
            Plano Inteligente Symplou
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: "-0.03em", mb: 2, fontSize: { xs: "2.2rem", md: "3.5rem" }, color: "#f8fafc" }}>
            Cresça sem <Box component="span" sx={{ background: SYMPL_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>pagar a mais</Box>
          </Typography>
          <Typography sx={{ color: "rgba(226,232,240,0.8)", fontSize: "1.1rem", maxWidth: 650, mx: "auto", lineHeight: 1.6 }}>
            Comece de graça e só invista quando sua operação exigir.
            O Symplou se molda ao tamanho do seu negócio.
          </Typography>
        </Box>

        {/* PACIFÍCOS RÁPIDOS */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {/* Comece Grátis */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: "rgba(15,23,42,0.8)",
                border: "1px solid rgba(148,163,184,0.3)",
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  borderColor: "rgba(148,163,184,0.6)",
                  transform: "translateY(-4px)"
                }
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(148,163,184,0.15)", color: "#94a3b8" }}>
                    <RocketLaunchIcon />
                  </Avatar>
                  <Typography sx={{ textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800, fontSize: "0.75rem", color: "#94a3b8" }}>
                    Para pequenos lojistas
                  </Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: "#f8fafc" }}>Comece Gratuito</Typography>
                <Typography sx={{ color: "rgba(226,232,240,0.7)", mb: 3, fontSize: "1rem", lineHeight: 1.6, flexGrow: 1 }}>
                  Ideal para validar sua loja e organizar as vendas. Inclui até 100 produtos, vendas ilimitadas, fechamento de caixa, 3 acessos e até 5 clientes no fiado. Sem amarras.
                </Typography>
                <Typography sx={{ fontSize: "2.5rem", fontWeight: 900, color: "#f8fafc", mb: 3 }}>
                  R$ 0<Typography component="span" sx={{ fontSize: "1rem", color: "rgba(226,232,240,0.5)", fontWeight: 700 }}>/mês para sempre</Typography>
                </Typography>
                <Button
                  onClick={handleNavigateFree}
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "rgba(148,163,184,0.5)",
                    color: "#f8fafc",
                    fontWeight: 800,
                    py: 1.5,
                    "&:hover": { borderColor: "#f8fafc", background: "rgba(255,255,255,0.05)" }
                  }}
                >
                  Começar agora mesmo
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Pacote Completo */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                bgcolor: "rgba(15,23,42,0.9)",
                border: "1px solid #14b8a6",
                borderRadius: 4,
                boxShadow: "0 16px 48px rgba(13,148,136,0.25)",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)"
                }
              }}
            >
              <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: SYMPL_GRADIENT, opacity: 0.1, zIndex: 0 }} />
              <CardContent sx={{ position: "relative", zIndex: 1, p: { xs: 3, md: 4 }, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: "rgba(20,184,166,0.15)", color: "#14b8a6" }}>
                    <WorkspacePremiumIcon />
                  </Avatar>
                  <Typography sx={{ textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800, fontSize: "0.75rem", color: "#14b8a6" }}>
                    Recomendação Symplou
                  </Typography>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, color: "#f8fafc" }}>Pacote Completo</Typography>
                <Typography sx={{ color: "rgba(226,232,240,0.8)", mb: 3, fontSize: "1rem", lineHeight: 1.6, flexGrow: 1 }}>
                  Leve toda a potência do sistema. Usuários, vendas e clientes ilimitados, IA, controle financeiro e comissões.
                  Você ganha <strong style={{ color: "#2dd4bf" }}>{fullBundlePreview.discountPct}% OFF</strong> e
                  <strong style={{ color: "#2dd4bf" }}> R$ {EXCLUSIVE_FULL_BUNDLE_BONUS_BRL},00</strong> em crédito de IA inicial.
                </Typography>

                <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 0.5 }}>
                  <Typography sx={{ color: "rgba(226,232,240,0.5)", textDecoration: "line-through", fontWeight: 600 }}>
                    R$ {fullBundlePreview.paidSubtotal.toFixed(2).replace(".", ",")}
                  </Typography>
                  <Chip label={`-${fullBundlePreview.discountPct}%`} size="small" sx={{ bgcolor: "rgba(20,184,166,0.2)", color: "#2dd4bf", fontWeight: 800 }} />
                </Stack>
                <Typography sx={{ fontSize: "2.5rem", fontWeight: 900, color: "#14b8a6", mb: 3 }}>
                  R$ {fullBundlePreview.finalMonthlyTotal.toFixed(2).replace(".", ",")}
                  <Typography component="span" sx={{ fontSize: "1rem", color: "rgba(226,232,240,0.7)", fontWeight: 700 }}>/mês</Typography>
                </Typography>

                <Button
                  onClick={handleNavigateFull}
                  variant="contained"
                  size="large"
                  sx={{
                    background: SYMPL_GRADIENT,
                    fontWeight: 800,
                    py: 1.5,
                    boxShadow: "0 8px 24px rgba(13,148,136,0.4)"
                  }}
                >
                  Assinar Pacote Completo
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* DIVISOR CUSTOMIZADO */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 8, opacity: 0.8 }}>
          <Box sx={{ flexGrow: 1, height: "1px", background: "linear-gradient(90deg, transparent, rgba(20,184,166,0.3))" }} />
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 3 }}>
            <TuneIcon sx={{ color: "#14b8a6" }} />
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#f8fafc", letterSpacing: "-0.02em" }}>
              Ou monte como você quiser
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1, height: "1px", background: "linear-gradient(270deg, transparent, rgba(20,184,166,0.3))" }} />
        </Box>

        {/* ÁREA DE SELEÇÃO CUSTOMIZADA */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              {variantGroups.map((group) => (
                <Box key={group.id} sx={{ border: "1px solid rgba(20,184,166,0.15)", borderRadius: 4, background: "rgba(15,23,42,0.5)", p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, color: "#f8fafc" }}>{group.title}</Typography>
                  <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.95rem", mb: 3 }}>{group.description}</Typography>
                  <Stack spacing={2}>
                    {group.variants.map((variant) => {
                      const selected = selectedVariants[group.id] === variant.id;
                      const isFreeVariant = variant.monthlyPrice === 0;

                      return (
                        <span key={variant.id} style={{ display: "block" }}>
                          <Card
                            elevation={0}
                            sx={{
                              bgcolor: selected ? "rgba(13,148,136,0.1)" : "rgba(2,6,23,0.4)",
                              border: "1px solid",
                              borderColor: selected ? "#14b8a6" : "rgba(148,163,184,0.15)",
                              borderRadius: 3,
                              transition: "all 0.2s ease",
                            }}
                          >
                            <CardActionArea
                              onClick={() => setSelectedVariants((prev) => ({ ...prev, [group.id]: variant.id }))}
                              sx={{ p: 2 }}
                            >
                              <Stack direction="row" alignItems="flex-start" gap={2}>
                                {selected
                                  ? <CheckCircleRoundedIcon sx={{ color: "#14b8a6", mt: 0.2, flexShrink: 0 }} />
                                  : <RadioButtonUncheckedRoundedIcon sx={{ color: "rgba(148,163,184,0.5)", mt: 0.2, flexShrink: 0 }} />
                                }
                                <Box sx={{ flex: 1 }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                      <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: selected ? "#2dd4bf" : "#f8fafc" }}>
                                        {variant.name}
                                      </Typography>
                                      {isFreeVariant && (
                                        <Chip
                                          label="Incluso"
                                          size="small"
                                          sx={{
                                            bgcolor: "rgba(20,184,166,0.15)",
                                            color: "#2dd4bf",
                                            fontWeight: 700,
                                            fontSize: "0.68rem",
                                            height: 20,
                                          }}
                                        />
                                      )}
                                    </Stack>
                                    <Typography sx={{ fontWeight: 800, color: selected ? "#14b8a6" : "#e2e8f0" }}>
                                      {isFreeVariant ? "Grátis" : `R$ ${variant.monthlyPrice.toFixed(2).replace(".", ",")}`}
                                    </Typography>
                                  </Stack>
                                  <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                                    {variant.description}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardActionArea>
                          </Card>
                        </span>
                      );
                    })}
                  </Stack>
                </Box>
              ))}

              <Box sx={{ border: "1px solid rgba(20,184,166,0.15)", borderRadius: 4, background: "rgba(15,23,42,0.5)", p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, color: "#f8fafc" }}>Módulos Adicionais (Add-ons)</Typography>
                <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.95rem", mb: 3 }}>
                  Incremente sua operação e veja o desconto do pacote aumentar.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {paidOrOptionalAddons.map((mod) => {
                    const selected = selectedAddons.includes(mod.id);
                    return (
                      <Grid item xs={12} sm={6} key={mod.id}>
                        <Card
                          elevation={0}
                          sx={{
                            height: "100%",
                            bgcolor: selected ? "rgba(13,148,136,0.1)" : "rgba(2,6,23,0.4)",
                            border: "1px solid",
                            borderColor: selected ? "#14b8a6" : "rgba(148,163,184,0.15)",
                            borderRadius: 3,
                            transition: "all 0.2s ease"
                          }}
                        >
                          <CardActionArea onClick={() => toggleAddon(mod.id)} sx={{ p: 2, height: "100%" }}>
                            <Stack direction="row" alignItems="flex-start" gap={1.5}>
                              {selected ?
                                <CheckCircleRoundedIcon sx={{ color: "#14b8a6", mt: 0.2 }} /> :
                                <RadioButtonUncheckedRoundedIcon sx={{ color: "rgba(148,163,184,0.5)", mt: 0.2 }} />
                              }
                              <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: selected ? "#2dd4bf" : "#f8fafc", mb: 0.5 }}>
                                  {mod.name}
                                </Typography>
                                <Typography sx={{ color: "rgba(226,232,240,0.65)", fontSize: "0.8rem", mb: 1, lineHeight: 1.4, minHeight: 36 }}>
                                  {mod.description}
                                </Typography>
                                <Typography sx={{ fontWeight: 800, color: selected ? "#14b8a6" : "#e2e8f0", fontSize: "0.9rem" }}>
                                  + R$ {mod.monthlyPrice.toFixed(2).replace(".", ",")}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Box sx={{ border: "1px dashed rgba(20,184,166,0.4)", borderRadius: 3, p: 2.5, bgcolor: "rgba(13,148,136,0.05)" }}>
                  <Typography sx={{ fontWeight: 800, mb: 1.5, color: "#14b8a6", fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Módulos Básicos Inclusos (Sempre Ativos)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {freeFixedModules.map((mod) => (
                      <Chip
                        key={mod.id}
                        label={mod.name}
                        icon={<CheckCircleRoundedIcon sx={{ color: "#14b8a6 !important" }} />}
                        size="small"
                        sx={{
                          bgcolor: "rgba(20,184,166,0.1)",
                          border: "1px solid rgba(20,184,166,0.2)",
                          color: "#f8fafc",
                          fontWeight: 600
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                position: "sticky",
                top: 88,
                bgcolor: "rgba(15,23,42,0.8)",
                border: "1px solid rgba(20,184,166,0.3)",
                boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                backdropFilter: "blur(12px)",
                borderRadius: 4
              }}
            >
              <Box sx={{ p: 3, borderBottom: "1px solid rgba(20,184,166,0.1)" }}>
                <Typography sx={{ textTransform: "uppercase", letterSpacing: "0.1em", color: "#14b8a6", fontWeight: 800, fontSize: "0.75rem", mb: 0.5 }}>
                  Resumo da sua assinatura
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: "#f8fafc" }}>
                  Plano Customizado
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {selectedIds.length === freeFixedIds.length ? (
                  <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.95rem", mb: 3 }}>
                    Selecione módulos nas áreas ao lado para calcular seu desconto e mensalidade.
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.85rem", mb: 1.5 }}>
                      <strong style={{ color: "#f8fafc" }}>{preview.selected.length}</strong> módulos integrados:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                      {selectedIds.slice(0, 5).map((id) => (
                        <Chip
                          key={id}
                          label={MODULE_PLANS.find((m) => m.id === id)?.name || id}
                          size="small"
                          sx={{
                            color: "rgba(226,232,240,0.9)",
                            border: "1px solid rgba(148,163,184,0.2)",
                            bgcolor: "rgba(2,6,23,0.5)",
                            fontSize: "0.75rem"
                          }}
                        />
                      ))}
                      {selectedIds.length > 5 && (
                        <Chip
                          label={`+${selectedIds.length - 5} módulos`}
                          size="small"
                          sx={{ color: "#2dd4bf", border: "1px solid rgba(20,184,166,0.3)", bgcolor: "rgba(13,148,136,0.1)", fontSize: "0.75rem", fontWeight: 700 }}
                        />
                      )}
                    </Stack>

                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.9rem" }}>Soma dos módulos pagos</Typography>
                        <Typography sx={{ fontWeight: 600, color: "#f8fafc" }}>R$ {preview.paidSubtotal.toFixed(2).replace(".", ",")}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.9rem" }}>Desconto progressivo</Typography>
                        <Chip label={`- ${preview.discountPct}%`} size="small" sx={{ bgcolor: "rgba(20,184,166,0.15)", color: "#2dd4bf", fontWeight: 800, borderRadius: 1 }} />
                      </Stack>
                    </Stack>
                  </>
                )}

                <Divider sx={{ borderColor: "rgba(148,163,184,0.1)", mb: 2 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 3 }}>
                  <Typography sx={{ color: "rgba(226,232,240,0.9)", fontSize: "1rem", fontWeight: 700 }}>Total por mês</Typography>
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 900, color: "#14b8a6", lineHeight: 1 }}>
                    R$ {preview.finalMonthlyTotal.toFixed(2).replace(".", ",")}
                  </Typography>
                </Stack>

                <Button
                  component={RouterLink}
                  to={`/assinar${customQuery}`}
                  fullWidth
                  variant={selectedIds.length > freeFixedIds.length ? "contained" : "outlined"}
                  disabled={selectedIds.length === freeFixedIds.length}
                  size="large"
                  sx={{
                    ...(selectedIds.length > freeFixedIds.length ? { background: SYMPL_GRADIENT } : { borderColor: "rgba(148,163,184,0.3)", color: "rgba(226,232,240,0.5)" }),
                    fontWeight: 800,
                    py: 1.5
                  }}
                >
                  Continuar Contratação
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>

      </Box>
    </MarketingShell>
  );
};
